package com.havn.app

import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.media.HavnPickedPhoto
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import kotlin.math.max
import kotlin.math.roundToInt

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    ComposeViewport(viewportContainerId = "ComposeTarget") {
        HavnApp(photoPicker = webPhotoPicker())
    }
}

private fun webPhotoPicker(): HavnPhotoPicker = HavnPhotoPicker { onResult ->
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*"
    input.style.display = "none"
    input.onchange = {
        val file = input.files?.item(0)
        if (file == null) {
            onResult(null)
            input.remove()
        } else {
            val bytesReader = FileReader()
            bytesReader.onload = {
                val arrayBuffer = bytesReader.result as ArrayBuffer
                val bytes = Uint8Array(arrayBuffer).toByteArray()
                val dataReader = FileReader()
                dataReader.onload = {
                    val dataUrl = dataReader.result as? String
                    createOptimizedPhotoPreview(dataUrl) { preview ->
                        onResult(
                            HavnPickedPhoto(
                                fileName = file.name.ifBlank { "havn-photo.jpg" },
                                bytes = bytes,
                                contentType = file.type.ifBlank { "image/jpeg" },
                                thumbnailDataUrl = preview?.dataUrl ?: dataUrl,
                                thumbnailWidth = preview?.width,
                                thumbnailHeight = preview?.height
                            )
                        )
                        input.remove()
                    }
                }
                dataReader.onerror = {
                    onResult(null)
                    input.remove()
                }
                dataReader.readAsDataURL(file)
            }
            bytesReader.onerror = {
                onResult(null)
                input.remove()
            }
            bytesReader.readAsArrayBuffer(file)
        }
    }
    document.body?.appendChild(input)
    input.click()
}

private data class OptimizedPhotoPreview(
    val dataUrl: String,
    val width: Int,
    val height: Int
)

private fun createOptimizedPhotoPreview(dataUrl: String?, onResult: (OptimizedPhotoPreview?) -> Unit) {
    if (dataUrl.isNullOrBlank()) {
        onResult(null)
        return
    }
    val image = document.createElement("img") as HTMLImageElement
    image.onload = {
        val originalWidth = image.naturalWidth.takeIf { it > 0 } ?: image.width
        val originalHeight = image.naturalHeight.takeIf { it > 0 } ?: image.height
        if (originalWidth <= 0 || originalHeight <= 0) {
            onResult(null)
        } else {
            val scale = minOf(1.0, HavnUploadPreviewMaxWidth / max(originalWidth, originalHeight).toDouble())
            val width = max(1, (originalWidth * scale).roundToInt())
            val height = max(1, (originalHeight * scale).roundToInt())
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = width
            canvas.height = height
            canvas.getContext("2d")
                ?.asDynamic()
                ?.drawImage(image, 0, 0, width, height)
            onResult(
                OptimizedPhotoPreview(
                    dataUrl = canvas.toDataURL("image/jpeg", HavnUploadPreviewJpegQuality),
                    width = width,
                    height = height
                )
            )
        }
        Unit
    }
    image.onerror = { _, _, _, _, _ ->
        onResult(null)
        Unit
    }
    image.src = dataUrl
}

private const val HavnUploadPreviewMaxWidth = 1_280
private const val HavnUploadPreviewJpegQuality = 0.78

private fun Uint8Array.toByteArray(): ByteArray =
    ByteArray(length) { index -> (this.asDynamic()[index] as Int).toByte() }
